import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Plus, Trash2, Copy, Edit3, Calendar, Clock,
  MoreVertical, Search, Filter, Grid, List, Download
} from 'lucide-react';
import reportBuilderService from '../services/reportBuilderService';

const MyReportsPage = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [menuOpen, setMenuOpen] = useState(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const data = await reportBuilderService.getTemplates();
      setTemplates(data.templates || []);
    } catch (err) {
      console.error('Failed to load templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await reportBuilderService.deleteTemplate(id);
      setTemplates(prev => prev.filter(t => t.id !== id));
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Failed to delete template:', err);
    }
  };

  const handleDuplicate = async (id) => {
    try {
      const data = await reportBuilderService.duplicateTemplate(id);
      if (data.template) {
        setTemplates(prev => [data.template, ...prev]);
      }
      setMenuOpen(null);
    } catch (err) {
      console.error('Failed to duplicate template:', err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const filteredTemplates = templates.filter(t =>
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (t.category || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getWidgetCount = (t) => {
    try {
      const w = typeof t.widgets === 'string' ? JSON.parse(t.widgets) : t.widgets;
      return Array.isArray(w) ? w.length : 0;
    } catch { return 0; }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">My Reports</h1>
            <p className="text-sm text-gray-500 mt-1">Create, manage, and export your custom reports</p>
          </div>
          <button
            onClick={() => navigate('/report-builder')}
            className="flex items-center gap-2 px-4 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium text-sm shadow-sm"
          >
            <Plus size={18} />
            New Report
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="px-6 py-3 bg-white border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search reports..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">{filteredTemplates.length} report{filteredTemplates.length !== 1 ? 's' : ''}</span>
            <div className="flex bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <Grid size={16} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <List size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full"></div>
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <FileText size={28} className="text-blue-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              {searchTerm ? 'No reports found' : 'No reports yet'}
            </h3>
            <p className="text-gray-500 mb-6 max-w-sm mx-auto">
              {searchTerm ? 'Try a different search term' : 'Create your first custom report with our drag-and-drop builder'}
            </p>
            {!searchTerm && (
              <button
                onClick={() => navigate('/report-builder')}
                className="px-5 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium text-sm"
              >
                Create Report
              </button>
            )}
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredTemplates.map(template => (
              <div
                key={template.id}
                className="bg-white rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer group relative"
                onClick={() => navigate(`/report-builder?id=${template.id}`)}
              >
                {/* Preview area */}
                <div className="h-36 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-t-xl flex items-center justify-center border-b border-gray-100">
                  <div className="text-center">
                    <FileText size={32} className="text-blue-300 mx-auto mb-1" />
                    <span className="text-xs text-blue-400 font-medium">{getWidgetCount(template)} widget{getWidgetCount(template) !== 1 ? 's' : ''}</span>
                  </div>
                </div>
                {/* Info */}
                <div className="p-3">
                  <h3 className="font-semibold text-gray-800 text-sm truncate">{template.name}</h3>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <Calendar size={11} />
                      {formatDate(template.updatedAt || template.updated_at)}
                    </span>
                    <span className="px-1.5 py-0.5 bg-gray-100 rounded text-xs text-gray-500 capitalize">{template.category || 'custom'}</span>
                  </div>
                </div>
                {/* Actions */}
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="relative">
                    <button
                      onClick={(e) => { e.stopPropagation(); setMenuOpen(menuOpen === template.id ? null : template.id); }}
                      className="p-1.5 bg-white rounded-lg shadow-sm border border-gray-200 hover:bg-gray-50"
                    >
                      <MoreVertical size={14} className="text-gray-500" />
                    </button>
                    {menuOpen === template.id && (
                      <div className="absolute right-0 top-8 bg-white rounded-lg shadow-lg border border-gray-200 py-1 w-36 z-10">
                        <button onClick={(e) => { e.stopPropagation(); navigate(`/report-builder?id=${template.id}`); }} className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"><Edit3 size={13} /> Edit</button>
                        <button onClick={(e) => { e.stopPropagation(); handleDuplicate(template.id); }} className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"><Copy size={13} /> Duplicate</button>
                        <button onClick={(e) => { e.stopPropagation(); setDeleteConfirm(template.id); setMenuOpen(null); }} className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"><Trash2 size={13} /> Delete</button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Widgets</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Updated</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredTemplates.map(template => (
                  <tr
                    key={template.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/report-builder?id=${template.id}`)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <FileText size={16} className="text-blue-400" />
                        <span className="font-medium text-gray-800 text-sm">{template.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3"><span className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600 capitalize">{template.category || 'custom'}</span></td>
                    <td className="px-4 py-3 text-sm text-gray-600">{getWidgetCount(template)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{formatDate(template.updatedAt || template.updated_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={(e) => { e.stopPropagation(); navigate(`/report-builder?id=${template.id}`); }} className="p-1.5 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded" title="Edit"><Edit3 size={14} /></button>
                        <button onClick={(e) => { e.stopPropagation(); handleDuplicate(template.id); }} className="p-1.5 text-gray-400 hover:text-green-500 hover:bg-green-50 rounded" title="Duplicate"><Copy size={14} /></button>
                        <button onClick={(e) => { e.stopPropagation(); setDeleteConfirm(template.id); }} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded" title="Delete"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setDeleteConfirm(null)}>
          <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4 shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 size={20} className="text-red-500" />
            </div>
            <h3 className="text-lg font-semibold text-center text-gray-800 mb-2">Delete Report?</h3>
            <p className="text-sm text-gray-500 text-center mb-6">This action cannot be undone. The report template and all its configuration will be permanently removed.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm)} className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyReportsPage;
